import React from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';

const LAST_UPDATED = '24 April 2026';

export const Privacy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Privacy Notice | Web Speed Advisor"
        description="Privacy notice for Web Speed Advisor, including how submitted URLs, analytics data and service diagnostics are processed."
        canonical="https://webspeedadvisor.com/privacy"
        robots="noindex, nofollow, noarchive"
      />

      <div className="bg-slate-900 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-black font-chivo">Privacy Notice</h1>
          <p className="text-slate-300 mt-2">Last reviewed: {LAST_UPDATED}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 prose prose-lg prose-slate">
        <p>
          This Privacy Notice explains how Web Speed Advisor collects, uses and protects information when
          you use the service. It is intended to be clear and practical, but it does not constitute legal advice.
        </p>

        <h2>What We Process</h2>
        <p>When you use the service, we may process the following categories of information:</p>
        <ul>
          <li>URLs submitted for analysis.</li>
          <li>Performance data returned by third-party analysis providers.</li>
          <li>Basic analytics and diagnostic information, such as browser type, device information and referrer data.</li>
          <li>Generated recommendations and implementation snippets associated with an analysed URL.</li>
          <li>
            <strong>GitHub OAuth data:</strong> when you connect a GitHub account, we receive your GitHub
            username, primary email address and repository list via GitHub OAuth. We store the GitHub App
            installation ID and repository metadata (name, visibility, default branch) to enable draft-PR
            functionality. We do not store source code content unless you explicitly submit a file or
            repository context as part of a draft-PR request.
          </li>
          <li>
            <strong>Authentication data:</strong> account creation and login are handled by Supabase, our
            authentication provider. Session tokens are stored in your browser (local storage / cookies) and
            your login credentials are processed by Supabase under{' '}
            <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">their own privacy policy</a>.
            We do not store raw passwords.
          </li>
          <li>
            <strong>Site context provided by you:</strong> optional context you supply about your site (such
            as technology stack or business description) is retained alongside analysis results and used to
            personalise recommendations.
          </li>
        </ul>

        <h2>How We Use This Information</h2>
        <p>We process this information to operate, maintain and improve the service, including to:</p>
        <ul>
          <li>run website performance tests and return results to you;</li>
          <li>generate recommendations, summaries and code snippets;</li>
          <li>monitor service reliability, abuse and technical faults;</li>
          <li>understand aggregate usage patterns and improve the product experience; and</li>
          <li>comply with legal or regulatory obligations where required.</li>
        </ul>

        <h2>Third-Party Services</h2>
        <p>To provide the service, we may send limited data to third-party providers, including:</p>
        <ul>
          <li>Google PageSpeed Insights, to run performance analysis against submitted URLs;</li>
          <li>
            AI inference providers, to generate recommendations, summaries and code snippets. When a
            page analysis is performed, the submitted URL and any site context you have provided are sent
            to the AI provider. No other personal data is included in these requests; and
          </li>
          <li>analytics providers, to measure product usage and service quality.</li>
        </ul>
        <p>
          Those providers process data under their own terms and privacy notices. We recommend reviewing
          their policies if you require further detail about their processing activities.
        </p>

        <h2>Retention</h2>
        <p>
          We aim to retain operational data only for as long as it is needed for service delivery,
          troubleshooting, abuse prevention or legitimate reporting. Cache and analytics retention periods
          may differ depending on technical requirements.
        </p>

        <h2>Cookies and Analytics</h2>
        <p>
          We may use cookies or similar technologies for analytics, service diagnostics and feature measurement.
          You can usually manage these settings through your browser controls.
        </p>

        <h2>Security</h2>
        <p>
          We use reasonable technical and organisational measures to protect information processed by the service.
          No internet-based service can be guaranteed to be completely secure, so you should avoid submitting
          confidential or highly sensitive URLs unless you are comfortable doing so.
        </p>

        <h2>Your Choices</h2>
        <p>Depending on your location and the way you use the service, you may be able to:</p>
        <ul>
          <li>stop using the service at any time;</li>
          <li>limit analytics through browser controls or consent tools, where available; and</li>
          <li>request further information about data handling via our website contact channel.</li>
        </ul>

        <h2>Children</h2>
        <p>
          The service is intended for professional or general business use and is not directed at children.
        </p>

        <h2>Changes To This Notice</h2>
        <p>
          We may update this Privacy Notice from time to time to reflect product, legal or operational changes.
          The latest version published on this page will apply from its stated review date.
        </p>

        <h2>Contact and Your Rights</h2>
        <p>
          If you have questions about this Privacy Notice or wish to exercise your data-subject rights —
          including, for EU and UK users, rights of access, rectification, erasure, restriction,
          portability and objection under the UK GDPR / EU GDPR — please contact us at{' '}
          <a href="mailto:info@wearecyborg.com">info@wearecyborg.com</a> or through the relevant support
          channel made available on the site. We will endeavour to respond within 30 days.
        </p>

        <div className="mt-8">
          <Link to="/" className="text-primary hover:text-primary-hover font-semibold">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
